import { cn } from '@/lib/utils'
import * as React from 'react'
import { TitleSection } from '../title-section'
import type { NavigationIcon } from './navigation-config'

/**
 * Context for the collapsible list panel animation.
 * - `isCollapsing`: true from t=0 when collapse starts (target state, immediate)
 * - `isCollapsed`: lags both directions — flips at width-transition end, so layout-only
 *   classes (flex-direction, display) only change while children are masked invisible
 * - `isAnimating`: true during the entire 250ms width transition in either direction;
 *   drives the symmetric fade-out/fade-in of children that mask the layout swap
 */
const CollapsedContext = React.createContext<{
  isCollapsing: boolean
  isCollapsed: boolean
  isAnimating: boolean
} | null>(null)

/** Returns `true` immediately when the panel starts collapsing (t=0). Use for opacity fades. */
export function useIsCollapsing() {
  return React.useContext(CollapsedContext)?.isCollapsing ?? false
}

/** Returns `true` only after the collapse animation finishes. Use for layout changes (icon-only). */
export function useIsCollapsed() {
  return React.useContext(CollapsedContext)?.isCollapsed ?? false
}

/** Returns `true` during the entire width transition (collapsing OR expanding). */
export function useIsAnimating() {
  return React.useContext(CollapsedContext)?.isAnimating ?? false
}

/** Returns `true` when this subtree is wrapped in a `CollapsibleListPanel` (left rail layout). */
export function useIsInCollapsibleListPanel() {
  return React.useContext(CollapsedContext) !== null
}

/**
 * PageLayout component that provides a consistent layout structure with:
 * - Fixed TitleSection at the top
 * - Content area below that fills remaining height
 *
 * This ensures the title stays visible and provides a container for sticky panels.
 * Note: This component does NOT create a scroll container. Individual panels manage their own scrolling.
 */
export function PageLayout(props: {
  icon: NavigationIcon
  title: string
  titleActions?: React.ReactNode
  breadcrumbsSlot?: React.ReactNode
  children?: React.ReactNode
}) {
  return (
    <div className="flex h-full flex-col print:h-auto print:overflow-visible">
      <TitleSection
        icon={props.icon}
        title={props.title}
        actions={props.titleActions}
        breadcrumbsSlot={props.breadcrumbsSlot}
      />
      <div className="flex min-h-0 flex-1 print:h-auto print:overflow-visible">{props.children}</div>
    </div>
  )
}

/**
 * StickyPanel component for side panels (left/middle) that should NOT scroll.
 * These panels contain fixed-height content or manage their own internal scrolling.
 */
export function StickyPanel(props: { children?: React.ReactNode; className?: string; width?: string }) {
  return <div className={cn('h-full overflow-hidden', props.width, props.className)}>{props.children}</div>
}

/**
 * StickyHeader component for headers/tabs that should stick to the top
 * of a ScrollableContent area (like ReportTabs).
 */
export function StickyHeader(props: { children?: React.ReactNode; className?: string }) {
  return <div className={cn('shrink-0 z-30', props.className)}>{props.children}</div>
}

/**
 * ScrollableArea component for content that should scroll independently
 * of any sticky headers in the same ScrollableContent parent.
 */
export function ScrollableArea(props: { children?: React.ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'scrollable-area scrollbar min-h-0 flex-1 overflow-auto print:h-auto [scrollbar-gutter:stable] print:overflow-visible',
        props.className,
      )}
    >
      {props.children}
    </div>
  )
}

/**
 * ScrollableContent component for the main content area (right panel).
 * This component provides scrollable content with proper overflow handling.
 */
export function ScrollableContent(props: { children?: React.ReactNode; className?: string }) {
  return (
    <div className={cn('min-w-0 flex-1 h-full flex flex-col print:h-auto print:overflow-visible', props.className)}>
      {props.children}
    </div>
  )
}

/**
 * SlideInPanel wraps detail content so it animates in from the right as the
 * sibling list panel contracts.
 */
export function SlideInPanel(props: {
  isOpen: boolean
  children?: React.ReactNode
  className?: string
  innerClassName?: string
}) {
  return (
    <div
      className={cn(
        '[--sip-left:0px] md:[--sip-left:52px]',
        'h-full shrink-0 overflow-hidden transition-[width] duration-250 ease-in-out print:h-auto print:overflow-visible',
        props.className,
      )}
      style={{ width: props.isOpen ? 'calc(100% - var(--sip-left))' : '0px' }}
    >
      <div className={cn('h-full', props.innerClassName)} style={{ width: 'calc(100cqw - var(--sip-left))' }}>
        {props.children}
      </div>
    </div>
  )
}

/**
 * CollapsibleListPanel is the companion to SlideInPanel for the list side: its outer
 * width transitions from `100%` down to `collapsedWidth` when a detail view opens,
 * while inner content stays at its natural width and is clipped by `overflow-hidden`.
 *
 * Provides two context values to descendants:
 * - `isCollapsing`: true from t=0 when collapse starts — use for opacity fades
 * - `isCollapsed`: true only after the transition ends — use for layout changes (icon-only mode)
 */
export function CollapsibleListPanel(props: { isOpen: boolean; children?: React.ReactNode; className?: string }) {
  const ref = React.useRef<HTMLDivElement>(null)
  const [isFullyCollapsed, setIsFullyCollapsed] = React.useState(props.isOpen)
  const [isAnimating, setIsAnimating] = React.useState(false)
  const prevOpenRef = React.useRef(props.isOpen)

  // Start animating the moment `isOpen` flips — set DURING RENDER (not in an effect) so `isAnimating`
  // commits in the SAME render as the new `isCollapsing` (= props.isOpen) value the context exposes.
  // An effect-based update lagged one render, during which descendants saw `isCollapsing` already
  // flipped while `isAnimating` was still false — and a consumer that snaps "when not animating"
  // (the toolbar) would then snap instantly, skipping the crossfade. Comparing against the previous
  // value (a ref, not a first-run flag) keeps a fresh mount in the same state from animating — a new
  // section mounts its panel expanded and should not crossfade — and is immune to double invocation
  // (StrictMode / transitions) since the value, not a flag, decides.
  //
  // `isFullyCollapsed` (exposed as `isCollapsed`) still lags to transition end so layout-only swaps
  // (flex-direction / display) happen while children opacity is at 0.
  if (prevOpenRef.current !== props.isOpen) {
    prevOpenRef.current = props.isOpen
    setIsAnimating(true)
  }

  // Settle the animation. `transitionend` (handler below) is the primary signal, but it can be
  // missed: the panel may be put offscreen/revealed by a Suspense boundary mid-transition (cold
  // navigate into a detail), the width may snap without an animatable change, or a backgrounded tab
  // may throttle the transition so the event never arrives. A missed settle leaves `isAnimating`
  // stuck true and `isFullyCollapsed` stale while the panel sits at its target width — descendants
  // then render out of sync with the rail. This guarantees convergence just after the 250ms
  // transition; when `transitionend` fires first, it's a redundant no-op.
  React.useEffect(() => {
    if (!isAnimating) return
    const settleTimer = setTimeout(() => {
      setIsAnimating(false)
      setIsFullyCollapsed(props.isOpen)
    }, 300)
    return () => clearTimeout(settleTimer)
  }, [isAnimating, props.isOpen])

  const handleTransitionEnd = (e: React.TransitionEvent) => {
    if (e.propertyName === 'width' && e.target === ref.current) {
      setIsAnimating(false)
      setIsFullyCollapsed(props.isOpen)
    }
  }

  const ctx = React.useMemo(
    () => ({ isCollapsing: props.isOpen, isCollapsed: isFullyCollapsed, isAnimating }),
    [props.isOpen, isFullyCollapsed, isAnimating],
  )

  return (
    <CollapsedContext.Provider value={ctx}>
      <div
        ref={ref}
        onTransitionEnd={handleTransitionEnd}
        className={cn(
          '@container/panel',
          '[--clp-collapsed:0px] md:[--clp-collapsed:52px]',
          'h-full shrink-0 overflow-hidden transition-[width] duration-250 ease-in-out print:h-auto print:overflow-visible',
          props.className,
        )}
        style={{ width: props.isOpen ? 'var(--clp-collapsed)' : '100%' }}
      >
        {props.children}
      </div>
    </CollapsedContext.Provider>
  )
}

/**
 * Wraps a single CollapsiblePanel child and adds `border-r-0` only when the list
 * panel is fully expanded (not collapsed AND not animating). This keeps the right
 * border visible during the expand/collapse transitions — otherwise the border would
 * vanish at t=0 of an expand and reappear after, creating a visible flash.
 */
export function CollapsibleListPanelBorder(props: { children: React.ReactElement<{ className?: string }> }) {
  const isCollapsed = useIsCollapsed()
  const isAnimating = useIsAnimating()
  const isFullyExpanded = !isCollapsed && !isAnimating
  return React.cloneElement(props.children, {
    className: cn(props.children.props.className, isFullyExpanded && 'border-r-0'),
  })
}
